<template>
	<div>
		<h2>Billing Address</h2>
		<label-required :label="'Name'" :condition="billing.full_name" />
		<input type="text" v-model="billing.full_name">
		<label-required :label="'Address'" :condition="billing.address_1" />
		<input type="text" v-model="billing.address_1">
		<div class="three-columns">
			<div>
				<label-required :label="'City'" :condition="billing.city" />
				<input type="text" v-model="billing.city">
			</div>
			<div>
				<label-required :label="'State'" :condition="billing.state" />
				<state-select :selectedState="billing.state" :showApo="true" :showProvinces="true" placeHolder="Select..." @updateSelected="updateSelectedState"></state-select>
			</div>
			<div>
				<label-required :label="'Zip Code'" :condition="billing.zip_code" />
				<input type="text" v-model="billing.zip_code">
			</div>
		</div>
		<div class="two-columns">
			<div>
				<label-required :label="'Email Address'" :condition="billing.email_address" />
				<input type="text" v-model="billing.email_address">
			</div>
			<div style="padding: 1.75em 0 0 0;">
				<label><i>Email is required for payment receipts and notifications.</i></label>
			</div>
		</div>

		<h2>Payment Info</h2>
		<div class="three-columns">
			<div>
				<label class="full">Card on File</label>
				<div v-if="billing.prepaid" style="padding: 0.5em 0 0 0;">
					<i><b>Prepaid</b></i>
				</div>
				<div v-else><b>{{ billing.reference_number || 'None' }}</b></div>
				<div v-if="!billing.prepaid && billing.expiration_month && billing.expiration_year">
					<label class="full v-spaced">Expiration</label>
					<b>{{ billing.expiration_month }}/{{ billing.expiration_year }}</b>
				</div>
				<div v-if="!billing.prepaid" class="v-spaced">
					<div :class="['button', billingIsComplete ? 'neutral' : 'disabled', 'small']" @click="setCardInputs()">{{ billing.reference_number ? 'Update' : 'Add' }} Card on File</div>
				</div>
			</div>
			<div>
				<label>Start Billing Date</label>
				<div class="input-with-tool-tip">
					<input type="date" v-model="billing.start_date" :min="minStartDate" :disabled="!showTypeControls" style="width: 10em;" />
					<tool-tip left='-10' :content="'The start billing date is the earliest this subscription will be charged.' + (showTypeControls ? ' Enter a future date to delay billing.' : '')"/>
				</div>
			</div>
			<div v-if="showTypeControls">
				<div class="component-with-tool-tip" style="padding: 1em 0 0 0;">
					<span :class="['pointer', 'far', billing.prepaid ? 'fa-check-square' : 'fa-square']" @click="billing.prepaid = !billing.prepaid"></span>
					<label> Prepaid</label>
					<tool-tip left='-6' content='Mark a subscription as prepaid if the credit card is billed through another system or if payment is made without a credit card.'/>
				</div>
				<div v-if="billing.prepaid && billing.vault_id != 'PREPAID'" style="margin: 0.5em 0 0 0;">
					NOTE: saving this subscription as prepaid will delete the current card on file.
				</div>
			</div>
		</div>

		<div v-if="editingCC" class="modal">
			<div class="inner">
				<h2>{{ billing.reference_number ? 'Update' : 'Add' }} Card on File</h2>

				<div v-if="getClient.is_demo" style="margin: 1em; background-color: #313131; color: white; padding: 0.5em; text-align: center;">
					<i class="fas fa-exclamation-triangle"></i> Flight Deck is in setup mode! <span style="cursor: pointer" @click="whyDemo = !whyDemo"><b><i>What's this?</i></b></span>
					<div v-if="whyDemo" style="margin: 1em 0 0 0;">
						The domain {{ hostname }} is for account setup.<br>
						Credit card updates will not be saved or processed<br>when you are in setup mode.
						<div v-if="getClient.primary_domain && getClient.primary_domain != hostname">
							You can log in to your live account at<br>
							<a :href="'https://' + getClient.primary_domain + '/login.php'" style="color: white;"><b>{{ getClient.primary_domain }}...</b></a>
						</div>
					</div>
				</div>

				<div v-if="billingIsComplete" class="content">
					<label-required :label="'Card Number'" :condition="localCCNumber" class="full" />
					<input type="text" v-model="localCCNumber" v-cardformat:formatCardNumber style="width: 20em;">
					<div class="two-columns">
						<div>
							<label-required :label="'Exp Month / Year'" :condition="localExpirationDate" />
							<div>
								<input type="text" v-model="localExpirationDate" placeholder="MM / YY" v-cardformat:formatCardExpiry style="width: 8em;">
							</div>
						</div>
						<div>
							<label-required :label="'Code'" :condition="localCVV" />
							<div class="input-with-tool-tip">
								<input type="text" v-model="localCVV" v-cardformat:formatCardCVC style="width: 4.5em;">
								<tool-tip left='-21' content='On most cards, the 3-digit security code is on the back, to the right of the signature. On American Express cards, the 4-digit security code is on the front, near the top-right of the card number.'/>
							</div>
						</div>
						<div v-if="showTypeControls">
							<label-required :label="'Start Billing Date'" :condition="billing.start_date" />
							<div class="input-with-tool-tip">
								<input type="date" v-model="localStartDate" :min="minStartDate" style="width: 10em;" />
								<tool-tip left='-10' content='The start billing date is the earliest this subscription will be charged. Enter a future date to delay billing.'/>
							</div>
						</div>
					</div>
					<div v-if="message" v-html="message" class="message center"></div>
				</div>
				<div v-else class="content">
					You need to enter a complete billing address<br>to update the card on file.
				</div>
				<div class="controls">
					<div class="button cancel spaced" @click="editingCC = false">Cancel</div>
					<div :class="['button', saving ? 'disabled' : 'action', 'spaced']" @click="savePayment()">{{ billing.reference_number ? 'Update' : 'Add' }} Card on File</div>
				</div>
			</div>
		</div>

	</div>
</template>
<script>
import Vue from 'vue'
import VueCardFormat from 'vue-credit-card-validation';
Vue.use(VueCardFormat);
import LabelRequired from '@/components/LabelRequired.vue'
import StateSelect from '@/components/StateSelect.vue'
import ToolTip from '@/components/ToolTip.vue'
export default {
	name: 'credit-card-inputs',
	components: {
		'label-required': LabelRequired,
		'state-select': StateSelect,
		'tool-tip': ToolTip
	},
	props: {
		billing: {
			type: [Object],
			required: true
		},
		enableAch: {
			type: [Boolean, String],
			required: false
		},
		lockCCInfo: {
			default: false,
			type: [Boolean, String],
			required: false
		},
		showTypeControls: {
			default: false,
			type: [Boolean, String],
			required: false
		}, 
		minStartDate: {
			type: String, 
			default: ''
		}
	},
	data() {
		return {
			message: '',
			expirationMonths: [],
			expirationYears: [],
			today: '',
			thisYear: '',
			editingCC: false,
			saving: false,
			ccLocked: false,
			localCCNumber: '',
			localExpirationDate: '',
			localCVV: '',
			whyDemo: false,
			hostname: window.location.hostname, 
			localStartDate: ''
		}
	},
	created() {
		this.today = this.$moment().format('YYYY-MM-DD')
		this.thisYear = new Date().getFullYear()
		for (let i = 0; i < 21; i++) {
			this.expirationYears.push(this.thisYear + i)
		}
	},
	computed: {
		billingIsComplete() {
			return this.billing.full_name && this.billing.address_1 && this.billing.city && this.billing.state && this.billing.zip_code && this.billing.email_address
		}
	},
	methods: {
		updateSelectedState(state) {
			this.billing.state = state
		},
		setCardInputs() {
			this.saving = false
			this.message = ''
			this.localStartDate = ''
			this.localCCNumber = ''
			this.localExpirationDate = ''
			this.localCVV = ''
			this.editingCC = true
		},
		savePayment() {
			if (this.saving) { return }
			this.message = ''
			if (!this.localCCNumber || this.localCCNumber.length < 15) {
				this.message = 'Credit Card Number is required.'
			} else if (!this.localExpirationDate || this.localExpirationDate.length < 7) {
				this.message = 'Expiration Month / Year is required.'
			} else if (!this.localCVV || this.localCVV.length < 3) {
				this.message = 'Code is required.'
			}
			let dateParts = this.localExpirationDate.split(' / ')
			let expirationMonth = dateParts[0]
			let expirationYear = dateParts[1]
			if (expirationYear.length == 2) {
				expirationYear = '20' + expirationYear
			}
			let today = new Date()
			let ccExpirationDate = new Date(expirationYear, expirationMonth, 0)
			if (ccExpirationDate < today) {
				this.message = 'Expiration date is expired.'
			}
			if (!this.localStartDate){
				this.message = 'Start billing date is required.'
			}
			if (this.message) { return }

			this.saving = true
			this.message = 'Saving billing address and card on file...'
			this.billing.start_date = this.localStartDate

			let postObject = {
				subscription: this.billing,
				credit_card: {
					reference_number: this.localCCNumber.replace(/\D/g,''),
					expiration_month: expirationMonth,
					expiration_year: expirationYear,
					cvv: this.localCVV
				}
			}

			this.apiCall({ destination: 'save_item_subscription', data: postObject }).then(obj => {
				if (obj.status == 'success') {
					this.message = 'Billing address and card on file saved!'
					this.billing.reference_number = obj.masked_number
					this.billing.expiration_month = expirationMonth
					this.billing.expiration_year = expirationYear
					
					setTimeout(() => {
						this.$emit('doAction', 'reload')
						this.message = ''
						this.editingCC = false
						this.localCCNumber = ''
						this.localExpirationDate = ''
						this.localCVV = ''
						this.saving = false
					}, 2000)
				} else {
					this.saving = false
					this.message = obj.message
				}
			}).catch(err => {
				console.log(err)
			})
		}
	}
}
</script>

<style lang="scss" scoped>
</style>
